import { Check, X } from "lucide-react";
import { API } from "@/lib/api";
import { optColor, praise, ResultTile } from "@/components/game/ResultTiles";
import { isStructured } from "@/lib/chem";
import { StructuredAnswer, StructuredResult } from "@/modes/quiz/Structured";

const STATUS_TEXT = { unanswered: "Yanıt vermedin", excluded: "Bu soruda bağlantın yoktu" };

export const QuestionHead = ({ state, q, remaining }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <span className="eyebrow">Soru {state.q_number || state.q_index} · {q.points} puan</span>
      {state.phase === "question" && <span className="font-display text-3xl font-black text-[#FACC15]" data-testid="student-timer">{Math.ceil(remaining / 1000)}</span>}
    </div>
    <p className="text-xl font-bold leading-snug" data-testid="student-question-text">{q.text}</p>
    {q.has_image && <img src={`${API}/play/${state.code}/image?q=${q.question_id}`} alt="Soru görseli" className="max-h-48 rounded-lg" />}
  </div>
);

const Answering = ({ state, send, remaining }) => {
  const q = state.question;
  const locked = Boolean(state.my_answer) || remaining <= 0;
  if (isStructured(q.type)) {
    return (
      <div className="space-y-5" data-testid="student-question">
        <QuestionHead state={state} q={q} remaining={remaining} />
        <StructuredAnswer key={q.question_id} state={state} send={send} remaining={remaining} />
        {state.my_answer && <p className="text-center text-[#6EE7B7] font-bold" data-testid="answer-received">Yanıtın alındı. Sonuç bekleniyor…</p>}
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="student-question">
      <QuestionHead state={state} q={q} remaining={remaining} />
      <div className="grid gap-3">
        {q.options.map((o) => {
          const picked = state.my_answer === o.id;
          return (
            <button key={o.id} disabled={locked} onClick={() => send({ type: "answer", option_id: o.id })} data-testid={`answer-${o.id}`}
              className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left text-lg font-bold transition-opacity duration-150 ${locked && !picked ? "opacity-40" : ""}`}
              style={{ borderColor: optColor(o), backgroundColor: picked ? `${optColor(o)}33` : "rgba(255,255,255,0.04)" }}>
              <span className="h-9 w-9 rounded-md grid place-items-center uppercase font-black text-[#05070D]" style={{ backgroundColor: optColor(o) }}>{o.id}</span>
              <span className="flex-1">{o.text}</span>
            </button>
          );
        })}
      </div>
      {state.my_answer && <p className="text-center text-[#6EE7B7] font-bold" data-testid="answer-received">Yanıtın alındı. Sonuç bekleniyor…</p>}
    </div>
  );
};

const Result = ({ state }) => {
  const r = state.results;
  if (r.board_reveal) {
    const right = state.question?.options?.find((o) => o.id === r.correct_option_id);
    return (
      <div className="space-y-2" data-testid="student-board-reveal">
        <p className="font-display text-2xl font-bold">Soru tahtada yanıtlandı.</p>
        {right && <p className="text-lg">Doğru cevap: <b className="text-[#4ADE80]">{right.id.toUpperCase()}) {right.text}</b></p>}
        <p className="text-sm text-slate-400">Bu sorudan puan verilmedi, sıradaki soru geliyor.</p>
      </div>
    );
  }
  if (r.skipped) return <p className="font-display text-2xl font-bold" data-testid="student-skipped">Bu soru atlandı.</p>;
  const mine = r.mine;
  const ok = mine?.status === "correct";
  const partial = mine?.status === "wrong" && mine.correct_n > 0;
  return (
    <div className="space-y-5" data-testid="student-result">
      <div key={`res-${state.q_index}`} className={`rounded-xl p-5 border-2 ${ok ? "border-[#34D399] bg-[#34D399]/10 anim-pop anim-sparkle" : mine?.status === "wrong" ? "border-red-500/70 bg-red-950/30 anim-shake" : "border-red-500/70 bg-red-950/30"}`}>
        <div className="flex items-center gap-3">
          {ok ? <Check className="h-8 w-8 text-[#34D399]" /> : <X className="h-8 w-8 text-red-400" />}
          <p className="font-display text-3xl font-black" data-testid="student-result-status">{partial ? "Az kaldı, kısmen doğru!" : ok || mine?.status === "wrong" ? praise(ok, state.q_index) : STATUS_TEXT[mine?.status] || "Bu soruya katılmadın"}</p>
        </div>
        {(ok || partial) && <p className="text-2xl font-bold text-[#6EE7B7] mt-2" data-testid="student-result-score">+{mine.score} puan{mine.total ? ` · ${mine.correct_n}/${mine.total} doğru` : ""}</p>}
        {r.hint && <p className="mt-3 text-base text-amber-200" data-testid="student-hint">İpucu: {r.hint}</p>}
      </div>
      {r.items ? <StructuredResult state={state} mine={mine} /> : <div className="space-y-2">
        <p className="eyebrow">Sınıfın yanıtları</p>
        {r.options.map((o) => <ResultTile key={o.id} o={o} correct={o.id === r.correct_option_id} mine={o.id === mine?.option_id} testId={`student-result-option-${o.id}`} />)}
        <p className="text-sm text-slate-400">Yanıtsız: %{r.unanswered_pct}</p>
      </div>}
      {r.explanation && <p className="text-slate-300">{r.explanation}</p>}
    </div>
  );
};

export const Totals = ({ state }) => (
  <div className="flex gap-3">
    <div className="panel flex-1 p-4"><p className="eyebrow">Toplam puan</p><p className="font-display text-3xl font-black text-[#FACC15]" data-testid="student-total">{state.my_total}</p></div>
    {state.rank && <div className="panel flex-1 p-4"><p className="eyebrow">Sıran</p><p className="font-display text-3xl font-black" data-testid="student-rank">{state.rank}</p></div>}
  </div>
);

const Final = ({ state }) => {
  const f = state.final;
  return (
    <div className="space-y-5" data-testid="student-final">
      <h2 className="font-display text-4xl font-black">Oyun bitti!</h2>
      <div className="panel p-5 space-y-2 text-lg">
        <p>Puanın: <b className="text-[#FACC15]" data-testid="student-final-score">{f.score}</b></p>
        {f.rank && <p>Sıran: <b data-testid="student-final-rank">{f.rank}</b></p>}
        <p>Doğru: <b className="text-[#6EE7B7]">{f.correct}</b> · Yanlış: <b>{f.wrong}</b> · Yanıtsız: <b>{f.unanswered}</b></p>
      </div>
    </div>
  );
};

export const QuizStudent = ({ state, send, remaining }) => {
  if (state.phase === "question") return <Answering state={state} send={send} remaining={remaining} />;
  if (state.phase === "results") return <div className="space-y-5"><QuestionHead state={state} q={state.question} />{state.results?.all_answered && <p className="rounded-xl border border-[#22C55E] bg-[#22C55E]/15 px-4 py-2 font-bold text-[#4ADE80]" data-testid="student-all-answered">Tüm öğrenciler soruyu yanıtladı!</p>}<Result state={state} /><Totals state={state} /></div>;
  if (state.phase === "finished") return <Final state={state} />;
  return null;
};
